import { action, computed, makeObservable, observable } from 'mobx';

import { ModelArrayCast, ModelCast } from 'shared/casts';
import { CalendarItemEducationTypeEnum, CalendarItemTypeEnum } from 'shared/enums';
import { toDate } from 'shared/utilities';
import { COLORS } from 'shared/constants';
import { ITaskModel, TaskModel } from 'shared/models/TaskTracker';

import { Model } from './Model';
import { UserModel, IUserModel } from './UserModel';
import { ReservationModel, IReservationModel } from './ReservationModel';

export interface ICalendarItemModel {
    id?: string;
    changeKey?: string;
    subject?: string;
    body?: string;
    typeId?: string | null;
    educationTypeId?: string | null;
    timezone?: string | null;
    startAt?: string | null;
    endAt?: string | null;
    isAllDayEvent?: boolean | number;
    reservationId?: number | null;
    reservation?: IReservationModel | null;
    isOrganizer?: boolean;
    organizer?: IUserModel;
    attendees?: IUserModel[];
    taskTrackerTask?: ITaskModel | null;
}

export class CalendarItemModel extends Model implements ICalendarItemModel {
    casts = {
        reservation: new ModelCast(ReservationModel),
        organizer: new ModelCast(UserModel),
        taskTrackerTask: new ModelCast(TaskModel),
        attendees: new ModelArrayCast(UserModel),
    };

    id = '';
    changeKey = '';
    subject = '';
    body = '';
    timezone = '';
    startAt: string | null = null;
    endAt: string | null = null;
    typeId = CalendarItemTypeEnum.Meeting;
    educationTypeId: string | null = null;
    isAllDayEvent = false;
    reminderMinutesBeforeStart = 0;
    reservationId = null;
    reservation = null as ReservationModel | null;
    isOrganizer = true;
    organizer = new UserModel();
    taskTrackerTask: TaskModel | null = null;
    attendees = [] as UserModel[];
    permissions = {
        canEdit: false,
        canDelete: false,
    };

    constructor(payload: ICalendarItemModel = {}) {
        super();

        makeObservable(this, {
            id: observable,
            typeId: observable,
            educationTypeId: observable,
            changeKey: observable,
            subject: observable,
            timezone: observable,
            body: observable,
            attendees: observable,
            startAt: observable,
            reminderMinutesBeforeStart: observable,
            endAt: observable,
            organizer: observable,
            taskTrackerTask: observable,
            reservation: observable,
            permissions: observable,
            isOrganizer: observable,
            isAllDayEvent: observable,
            color: computed,
            startAtDate: computed,
            startAtMinutes: computed,
            endAtDate: computed,
            endAtMinutes: computed,
            educationType: computed,
            type: computed,
            update: action,
        });

        this.update(payload);
    }

    get color() {
        if (this.typeId === CalendarItemTypeEnum.Reminder) {
            return COLORS._00A8FF;
        }

        if (this.typeId === CalendarItemTypeEnum.Task) {
            return COLORS._A55EEA;
        }

        if (this.typeId === CalendarItemTypeEnum.Education) {
            return COLORS._FFAA34;
        }

        return COLORS._1CBA4C;
    }

    get startAtDate() {
        if (!this.startAt) {
            return toDate();
        }
        return toDate(this.startAt);
    }

    get endAtDate() {
        if (!this.startAt) {
            return toDate();
        }
        return toDate(this.endAt);
    }

    get startAtMinutes() {
        return this.startAtDate.hour() * 60 + this.startAtDate.minute();
    }

    get endAtMinutes() {
        return this.endAtDate.hour() * 60 + this.endAtDate.minute();
    }

    get type() {
        return CalendarItemTypeEnum.fromValue(this.typeId);
    }

    get educationType() {
        return CalendarItemEducationTypeEnum.fromValue(this.educationTypeId);
    }
}
